import React, { createContext, useCallback, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showSuccessToast, showErrorToast } from "../../../utils/toast";
import {
    addToCart as reduxAddToCart,
    removeFromCart as reduxRemoveFromCart,
    clearCart as reduxClearCart,
    restoreCart as reduxRestoreCart,
    updateCartItem as reduxUpdateCartItem,
    bumpCartCount as reduxBumpCartCount,
    resetCartHighlight as reduxResetCartHighlight,
} from "../../../store/cartSlice";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const dispatch = useDispatch();
    const cart = useSelector((state) => state.cart.items);
    const cartCountHighlight = useSelector((state) => state.cart.cartCountHighlight);

    // Reset highlight after animation completes
    useEffect(() => {
        if (cartCountHighlight) {
            const timer = setTimeout(() => {
                dispatch(reduxResetCartHighlight());
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [cartCountHighlight, dispatch]);

    // Wrapper for addToCart with validation and toasts
    const addToCart = useCallback((product, qty = 1) => {
        console.log('Cart before add:', cart.map(i => ({ id: i.productId, name: i.name, qty: i.qty })));
        console.log('Adding product:', product.id, product.name, qty);

        // Check stock before adding
        const existingItem = cart.find((item) => item.productId === product.id);
        const newQty = existingItem ? existingItem.qty + qty : qty;

        const hasKnownStock = product.stock !== undefined && product.stock !== null && product.stock !== "";
        const availableStock = Number(product.stock);
        if (hasKnownStock && Number.isFinite(availableStock) && newQty > availableStock) {
            showErrorToast(`Only ${availableStock} left in stock`);
            return false;
        }

        dispatch(reduxAddToCart({ product, qty }));

        if (existingItem) {
            showSuccessToast(`${product.name} quantity updated`);
        } else {
            showSuccessToast(`${product.name} added to cart`);
        }

        dispatch(reduxBumpCartCount());
        console.log('Cart after add:', cart.map(i => ({ id: i.productId, name: i.name, qty: i.qty })));
        return true;
    }, [cart, dispatch]);

    // Wrapper for removeFromCart
    const removeFromCart = useCallback((lineId, removeAll = false) => {
        dispatch(reduxRemoveFromCart({ lineId, removeAll }));
    }, [dispatch]);

    // Wrapper for clearCart
    const clearCart = useCallback(() => {
        dispatch(reduxClearCart());
    }, [dispatch]);

    const restoreCart = useCallback((items = []) => {
        dispatch(reduxRestoreCart(items));
    }, [dispatch]);

    // Get item quantity by product ID
    const getItemQuantity = (productId) => {
        const item = cart.find((i) => i.productId === productId);
        return item ? item.qty : 0;
    };

    // Get total price of cart
    const getCartTotal = () => {
        return cart.reduce((sum, item) => sum + item.price * item.qty, 0);
    };

    // Update cart item properties
    const updateCartItem = useCallback((lineId, updates) => {
        dispatch(reduxUpdateCartItem({ lineId, updates }));
    }, [dispatch]);

    return (
        <CartContext.Provider
            value={{
                cart,
                addToCart,
                removeFromCart,
                clearCart,
                restoreCart,
                getItemQuantity,
                getCartTotal,
                cartCountHighlight,
                updateCartItem,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
